import { useAtom, useAtomValue } from "jotai";

import Button from "components/Button";
import Flex from "components/Flex";
import { CameraIcon } from "components/Icons/Camera";
import PageLayout from "components/PageLayout";
import PhotoGrid from "components/PhotoGrid";
import PhotoGridDocuments from "components/PhotoGridDocuments";
import PhotoUpload from "components/PhotoUpload";
import { Photo } from "components/PhotoUpload/types";
import Text from "components/Text";

import { amicableSignedAtom } from "atoms/accidentInformation";
import { caiPhotosAtom } from "atoms/caiPhotos";
import { useCheckpoint } from "hooks/useCheckpoint";
import { useClientTranslation } from "hooks/useClientTranslation";
import { useDigitalJourneyPageRouting } from "hooks/useDigitalJourneyPageRouting";
import { useEventTracking } from "hooks/useEventTracking";
import { Checkpoint } from "types/Checkpoint";
import { CaiEnum } from "types/DigitalJourneySession";
import { processImageForUpload } from "utils/imageProcessing/processImageForUpload";

export const DJPhotosCAIPage = () => {
  const { t } = useClientTranslation();
  const { trackEvent } = useEventTracking();
  const { onNext, continueCTA } = useDigitalJourneyPageRouting();
  const targetReached = useCheckpoint(Checkpoint.CLAIM_SUBMITTED);
  const [caiPhotos, setCaiPhotos] = useAtom(caiPhotosAtom);
  const cai = useAtomValue(amicableSignedAtom);

  const canContinue = cai === CaiEnum.ONE_SIGNED || (cai === CaiEnum.BOTH_SIGNED && caiPhotos.length > 0);

  const onContinue = () => {
    trackEvent({ name: "Digital Journey CAI Photos - continue", properties: { numberOfImages: caiPhotos.length } });
    onNext();
  };

  const onDrop = (files: File[]) => {
    files.forEach(async (file, index) => {
      const image = await processImageForUpload({
        file,
        alt: `CAI photo to upload number ${index + 1}`,
        type: Photo.Additional,
      });

      setCaiPhotos((photos) => [...photos, image]);
    });
  };

  return (
    <PageLayout
      contentSpacing="space-between"
      dataTestId="caiPhotos"
      forceContinue={targetReached}
      bottomButtons={
        <Flex gap={16}>
          <PhotoUpload onDrop={onDrop} disableGalleryUploadOverride={false}>
            <Button typeVariant={canContinue ? "secondary" : "primary"} leftIcon={<CameraIcon />}>
              {t("global.add_photos")}
            </Button>
          </PhotoUpload>
          {canContinue && <Button onClick={onContinue}>{continueCTA}</Button>}
        </Flex>
      }
    >
      <Flex mx={24} mb={32} my={8} gap={16}>
        <Flex gap={8}>
          <Text as="h1" sizeVariant="xl" weight={600}>
            Scatta una foto della Constatazione Amichevole (CAI)
          </Text>
          <Text>Fotografa il documento; qualora sia su più pagine, assicurati di fotografarle tutte.</Text>
        </Flex>
        {Boolean(caiPhotos.length) ? (
          <PhotoGrid images={caiPhotos} deleteFromAtom={setCaiPhotos} />
        ) : (
          <Flex
            align="center"
            sx={{
              "& > div": {
                width: "unset",
              },
            }}
          >
            <PhotoGridDocuments
              sxImageContainer={{
                "&&": {
                  maxHeight: "354px",
                },
              }}
              images={[
                {
                  id: "djCAIPhoto",
                  alt: "CAI Photos",
                  src: "/assets/png/digitalJourney/cai-example.png",
                },
              ]}
            />
          </Flex>
        )}
      </Flex>
    </PageLayout>
  );
};
